import { type ScopeContext, SentryError } from '@ifixit/sentry';
import { STOREFRONT_API_VERSION } from './env';
import { getSdk, type Requester, type VisitorConsent } from './generated/sdk';

export type ShopCredentials = {
   shopDomain: string;
   storefrontAccessToken: string;
};

export type VisitorTokens = { uniqueToken: string; visitToken: string };

export function getClientShopifyStorefrontSdk({
   shopDomain,
   storefrontAccessToken,
   getVisitorTokens,
   getVisitorConsent,
}: ShopCredentials & {
   getVisitorTokens?: () => VisitorTokens;
   getVisitorConsent?: () => VisitorConsent | null;
}) {
   const requester: Requester = async <R, V>(doc: string, variables: V): Promise<R> => {
      const body = {
         query: doc,
         variables: withVisitorConsent(doc, variables, getVisitorConsent?.()),
      };
      const response = await fetch(
         `https://${shopDomain}/api/${STOREFRONT_API_VERSION}/graphql.json`,
         {
            method: 'POST',
            headers: {
               'Content-Type': 'application/json',
               'X-Shopify-Storefront-Access-Token': storefrontAccessToken,
               ...(getVisitorTokens && getVisitorTrackingHeaders(getVisitorTokens())),
            },
            body: JSON.stringify(body),
         }
      );
      const result = await getResult<R>(response);
      if (result.errors && result.errors.length > 0) {
         let errorMessage = 'Storefront API query failed with errors: ';
         const errorMessages = result.errors.map(error => {
            const code = error.extensions?.code ?? 'UNKNOWN';
            return `[code: ${code}]: ${error.message}`;
         });
         errorMessage +=
            errorMessages.length > 1 ? errorMessages.join('\n\t - ') : errorMessages[0];
         console.error(errorMessage);
         const sentryDetails: Partial<ScopeContext> = {
            contexts: {
               graphql_response: result,
               body,
            },
            tags: {
               request_url: response.url,
               request_status: response.status.toString(),
               request_status_text: response.statusText,
            },
         };
         throw new SentryError(errorMessage, sentryDetails);
      }
      return result.data;
   };

   return getSdk(requester);
}

/**
 * Checkout can't read the consent cookies on our domain, so cart operations
 * declare $visitorConsent and Shopify adds the visitor's choice to the cart's
 * checkoutUrl as the `_cs` parameter.
 */
function withVisitorConsent<V>(
   doc: string,
   variables: V,
   visitorConsent: VisitorConsent | null | undefined
) {
   if (!visitorConsent || !doc.includes('$visitorConsent')) return variables;
   return { ...variables, visitorConsent };
}

function getVisitorTrackingHeaders({
   uniqueToken,
   visitToken,
}: VisitorTokens): Record<string, string> {
   if (!uniqueToken || !visitToken) return {};
   return { 'X-Shopify-UniqueToken': uniqueToken, 'X-Shopify-VisitToken': visitToken };
}

type ApiResult<R> = {
   data: R;
   errors?: Array<{
      message: string | null;
      path?: string[] | null;
      extensions?: {
         code: string | null;
      };
   }> | null;
};

async function getResult<R>(response: Response): Promise<ApiResult<R>> {
   if (!response.ok) {
      throw new SentryError(`GraphQL query failed to execute: ${response.statusText}`);
   }
   return response.json();
}
